// Thin React binding for PlaybackEngine. All playback logic lives in engine.ts; this file only
// owns the engine's lifetime and exposes its snapshot through useSyncExternalStore.

import { useEffect, useLayoutEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { PlayerAdapter } from '@nelnela/contracts';
import { PlaybackEngine, type EngineLine, type EngineState } from './engine';

const noopSubscribe = () => () => {};
const nullSnapshot = () => null;

/**
 * Builds a PlaybackEngine for `adapter` (rebuilt when the adapter identity changes, destroyed
 * on unmount) and returns its live snapshot. Both are null until an adapter exists — and for
 * one render after it appears, since construction happens in an effect.
 */
export function usePlaybackEngine(adapter: PlayerAdapter | null): {
  engine: PlaybackEngine | null;
  state: EngineState | null;
} {
  // Constructed in an effect, not useMemo: the constructor subscribes to the adapter, and
  // StrictMode's mount→cleanup→mount would otherwise destroy the memoised engine and never
  // rebuild it (or leave a ghost engine subscribed and double-firing auto-pause).
  const [engine, setEngine] = useState<PlaybackEngine | null>(null);
  useEffect(() => {
    if (!adapter) {
      setEngine(null);
      return;
    }
    const e = new PlaybackEngine(adapter);
    setEngine(e);
    return () => e.destroy();
  }, [adapter]);

  const subscribe = useMemo(
    () => (engine ? (cb: () => void) => engine.subscribe(cb) : noopSubscribe),
    [engine],
  );
  const getSnapshot = useMemo<() => EngineState | null>(
    () => (engine ? () => engine.getState() : nullSnapshot),
    [engine],
  );
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { engine, state };
}

/**
 * Pushes `lines` into the engine whenever the array identity (or the engine) changes.
 * useLayoutEffect, as in the local app: structural edits shift line indices and the boundary
 * tracker must be reconciled before the next tick, not after paint.
 */
export function useLines(engine: PlaybackEngine | null, lines: readonly EngineLine[]): void {
  useLayoutEffect(() => {
    engine?.setLines(lines);
  }, [engine, lines]);
}
